// Pickup Section component
import React from "react";
import { useTranslation } from "react-i18next";
import { FaCar, FaMapMarkerAlt, FaSyncAlt, FaClock } from "react-icons/fa";
import type { CartTheme } from "../types";
import MapContainer from "~/components/ui/MapContainer";

interface ArrivalTimeConfig {
    is_enabled: boolean;
    time_options: string[];
    default_selection: number;
}

interface PickupSectionProps {
    userLocation: string;
    isGettingLocation: boolean;
    getLocation: () => void;
    branch?: {
        location?: any;
        name?: string;
        logo?: string | null;
        address?: string | string[] | null;
    } | null;
    theme?: CartTheme | null;
    arrivalTimeConfig?: ArrivalTimeConfig | null;
    selectedArrivalTime?: string;
    onArrivalTimeChange?: (time: string) => void;
}

export const PickupSection: React.FC<PickupSectionProps> = ({
    userLocation,
    isGettingLocation,
    getLocation,
    branch,
    theme,
    arrivalTimeConfig,
    selectedArrivalTime,
    onArrivalTimeChange,
}) => {
    const { t } = useTranslation();

    const resolvedPrimaryColor = theme?.primaryColor || "#ea580c";
    const resolvedTextColor = theme?.textColor || "#1f2937";
    const resolvedBackgroundColor = theme?.backgroundColor || "#ffffff";

    return (
        <div className="space-y-4 pt-4">
                    {/* Arrival Time Selector */}
            {arrivalTimeConfig?.is_enabled && arrivalTimeConfig.time_options && (
                <div
                    className="p-4 rounded-lg"
                    style={{
                        backgroundColor: `${resolvedPrimaryColor}08`,
                        border: `1px solid ${resolvedPrimaryColor}30`,
                    }}
                >
                    <div className="flex items-start gap-3">
                        <FaClock
                            size={18}
                            style={{ color: resolvedPrimaryColor }}
                            className="mt-1"
                        />
                        <div className="flex-1">
                            <label
                                htmlFor="arrival-time"
                                className="block font-medium text-sm mb-2"
                                style={{ color: resolvedPrimaryColor }}
                            >
                                {t("menu.cart.expectedArrivalTime")}
                            </label>
                            <select
                                id="arrival-time"
                                value={selectedArrivalTime}
                                onChange={(e) => onArrivalTimeChange?.(e.target.value)}
                                className="w-full px-3 py-2 rounded-lg border text-sm outline-none transition-colors"
                                style={{
                                    borderColor: resolvedPrimaryColor,
                                    color: resolvedTextColor,
                                    backgroundColor: resolvedBackgroundColor,
                                }}
                            >
                                {arrivalTimeConfig.time_options.map((time, index) => {
                                    // Parse time format like "00:15" to "15 minutes"
                                    const [hours, minutes] = time.split(':');
                                    const totalMinutes = parseInt(hours) * 60 + parseInt(minutes);
                                    let displayText = '';
                                    
                                    if (totalMinutes < 60) {
                                        displayText = `${totalMinutes} ${t("menu.cart.minutes")}`;
                                    } else {
                                        const hrs = Math.floor(totalMinutes / 60);
                                        const mins = totalMinutes % 60;
                                        if (mins === 0) {
                                            displayText = `${hrs} ${t("menu.cart.hour")}${hrs > 1 ? 's' : ''}`;
                                        } else {
                                            displayText = `${hrs} ${t("menu.cart.hour")}${hrs > 1 ? 's' : ''} ${mins} ${t("menu.cart.minutes")}`;
                                        }
                                    }

                                    return (
                                        <option key={index} value={time}>
                                            {displayText}
                                        </option>
                                    );
                                })}
                            </select>
                            <p
                                className="text-xs mt-1"
                                style={{ color: `${resolvedTextColor}80` }}
                            >
                                {t("menu.cart.arrivalTimeHint")}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <div
                className="p-4 rounded-lg"
                style={{
                    backgroundColor: `${resolvedPrimaryColor}10`,
                    border: `1px solid ${resolvedPrimaryColor}40`,
                }}
            >
                <div className="flex items-start gap-3">
                    <FaCar
                        size={20}
                        style={{ color: resolvedPrimaryColor }}
                        className="mt-1"
                    />
                    <div>
                        <h3
                            className="font-medium text-sm mb-2"
                            style={{ color: resolvedPrimaryColor }}
                        >
                            {t("menu.cart.pickupInstructions")}
                        </h3>
                        <p
                            className="text-sm leading-relaxed"
                            style={{ color: resolvedTextColor }}
                        >
                            {t("menu.cart.pickupDetails")}
                        </p>
                        {branch?.address && (
                            <div
                                className="mt-2 p-2 rounded"
                                style={{ backgroundColor: resolvedBackgroundColor }}
                            >
                                <p
                                    className="text-sm font-medium flex items-center gap-2"
                                    style={{ color: resolvedTextColor }}
                                >
                                    <FaMapMarkerAlt size={12} />
                                    {Array.isArray(branch.address) ? branch.address.join(", ") : branch.address}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

    
            {/* Pickup Location Map */}
            {branch?.location && (
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <span
                            className="text-sm font-medium"
                            style={{ color: resolvedTextColor }}
                        >
                            {t("menu.cart.location.pickupLocation")}
                        </span>
                        <button
                            onClick={getLocation}
                            disabled={isGettingLocation}
                            className="flex items-center gap-1 px-2 py-1 text-xs rounded transition-all hover:opacity-80 disabled:opacity-50"
                            style={{
                                color: resolvedPrimaryColor,
                                backgroundColor: `${resolvedPrimaryColor}15`,
                            }}
                        >
                            <FaSyncAlt
                                size={10}
                                className={isGettingLocation ? "animate-spin" : ""}
                            />
                            {isGettingLocation
                                ? t("menu.cart.location.getting")
                                : userLocation
                                    ? t("menu.cart.location.refresh")
                                    : t("menu.cart.location.get")}
                        </button>
                    </div>
                    <MapContainer
                        restaurantLocation={branch?.location}
                        userLocation={userLocation}
                        restaurantName={branch?.name}
                        restaurantLogo={branch?.logo ?? undefined}
                        placeId={branch?.location?.place_id}
                        theme={{
                            primaryColor: resolvedPrimaryColor,
                            textColor: resolvedTextColor,
                            backgroundColor: resolvedBackgroundColor,
                        }}
                        title={""}
                        subtitle={t("menu.cart.location.navigateToRestaurant")}
                        showForPickup={true}
                        onGetUserLocation={getLocation}
                    />
                </div>
            )}
        </div>
    );
};

export default PickupSection;
